import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Capacitor } from '@capacitor/core'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { supabase } from '../lib/supabase'

const BUG_REPORT_EMAIL = 'contactus@netherfire.com'

type LoginStatus = 'idle' | 'sending' | 'sent' | 'error'

/**
 * 账号页面：登录入口 + Bug 提交入口
 */
export function AccountPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<LoginStatus>('idle')

  const handleLogin = async () => {
    if (!supabase || !email) return
    setStatus('sending')
    const { error } = await supabase.auth.signInWithOtp({ email })
    setStatus(error ? 'error' : 'sent')
  }

  const handleReportBug = () => {
    const platform = Capacitor.getPlatform()
    const subject = encodeURIComponent(`[Blaze Grids] Bug Report (${platform})`)
    const body = encodeURIComponent(
      `Platform: ${platform}\nApp version: 1.0.0\n\nWhat happened:\n\n\nSteps to reproduce:\n`
    )
    window.location.href = `mailto:${BUG_REPORT_EMAIL}?subject=${subject}&body=${body}`
  }

  return (
    <div className="min-h-screen flex flex-col bg-zinc-950 text-white">
      <Navbar />

      <main className="flex-1 w-full max-w-md mx-auto px-4 py-10">
        <h1 className="text-2xl font-bold mb-8 text-center">{t('account.title')}</h1>

        {/* 登录卡片 */}
        <section className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-brand-orange mb-2">{t('account.login')}</h2>
          <p className="text-zinc-400 text-sm mb-4">{t('account.login_desc')}</p>

          {supabase ? (
            <>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('account.email_placeholder')}
                className="w-full mb-3 px-4 py-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:outline-none focus:border-brand-orange"
              />
              <button
                onClick={handleLogin}
                disabled={!email || status === 'sending'}
                className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'sending' ? t('account.sending') : t('account.login')}
              </button>
              {status === 'sent' && (
                <p className="text-green-400 text-sm mt-3">{t('account.link_sent')}</p>
              )}
              {status === 'error' && (
                <p className="text-red-400 text-sm mt-3">{t('account.login_error')}</p>
              )}
            </>
          ) : (
            <button disabled className="w-full btn btn-secondary opacity-50 cursor-not-allowed">
              {t('account.login')}
            </button>
          )}
          {!supabase && (
            <p className="text-zinc-500 text-xs mt-3">{t('account.coming_soon')}</p>
          )}
        </section>

        {/* Bug 提交卡片 */}
        <section className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-brand-red mb-2">{t('account.report_bug_title')}</h2>
          <p className="text-zinc-400 text-sm mb-4">{t('account.report_bug_desc')}</p>
          <button onClick={handleReportBug} className="w-full btn btn-secondary">
            🐞 {t('account.report_bug')}
          </button>
          <p className="text-zinc-500 text-xs mt-3">{t('account.email_client_hint')}</p>
        </section>
      </main>

      <Footer />
    </div>
  )
}
